// src/utils/zodiacMemes.ts
// Data for the Zodiac Meme Library section on the landing page.
// Locked meme format: "Tell Me Without Telling Me...[Sign] — setup → punchline"
// + closing tagline.
//
// Each `symbol` ends in U+FE0E (VARIATION SELECTOR-15) to force text
// presentation — otherwise Windows renders the zodiac code points as
// full-color emoji (purple squares) that ignore CSS `color`.
//
// `article` is "a" or "an" depending on the sign name's leading vowel
// sound — render copy as `{article} {name}` so Aries/Aquarius read
// "an Aries" / "an Aquarius" without hardcoded grammar logic at the
// call site.

// `\n` is rendered as a hard line break by the card footer
// (cardTaglineStyle uses `whiteSpace: "pre-line"`).
export const TAGLINE = "Crushhh because\nsomebody needs a hint.";

export interface Meme {
  n: number;
  setup: string;
  punchline: string;
}

export interface ZodiacSignMemes {
  name: string;
  symbol: string;
  dates: string;
  element: "Fire" | "Earth" | "Air" | "Water";
  color: string;
  /** "a" or "an" — chosen by the sign name's leading vowel sound. */
  article: "a" | "an";
  /** URL to Zodiyak mascot SVG (served from /public). */
  image: string;
  memes: Meme[];
}

export const SIGNS: ZodiacSignMemes[] = [
  {
    name: "Aries", symbol: "\u2648\uFE0E", dates: "Mar 21 \u2013 Apr 19",
    element: "Fire", color: "#E05A3A", article: "an",
    image: "/SVG/zodiyak-aries.svg",
    memes: [
      {
        n: 1,
        setup: "Text them something is wrong but say never mind, it's fine",
        punchline: "No. Say it. What's wrong. Tell me. I'm not dropping this. We are not doing \"I'm fine.\" What happened…I'm coming over…",
      },
      {
        n: 2,
        setup: "Beat them at something they're good at",
        punchline: "*slow nod*\nOkay. Good game. Didn't know you uh…played…nice…skills. Goood job…\n*rematch immediately*",
      },
      {
        n: 3,
        setup: "Ask them why they're upset",
        punchline: "I'm not upset. I just think it's interesting that you would do that. That's all. It's fine. I'm fine. Moving on...\n*is not moving on*",
      },
      {
        n: 4,
        setup: "Invite them somewhere, tell them it's totally their call",
        punchline: "We're going.",
      },
      {
        n: 5,
        setup: "Ask them Thursday to do something Friday night across town",
        punchline: "Yeah. What time. I'll drive.",
      },
    ],
  },
  {
    name: "Taurus", symbol: "\u2649\uFE0E", dates: "Apr 20 \u2013 May 20",
    element: "Earth", color: "#E8714E", article: "a",
    image: "/SVG/zodiyak-taurus.svg",
    memes: [
      {
        n: 1,
        setup: "Suggest trying a new restaurant instead of the usual spot",
        punchline: "Why.",
      },
      {
        n: 2,
        setup: "Rearrange their living room to help out",
        punchline: "*walks in, stops*\nWhere's my thing.\n*finds it*\nWhy is it there.\n*moves it back*",
      },
      {
        n: 3,
        setup: "Tell them the trip involves a lot of walking",
        punchline: "How much walking? Hmmm…when is it?\n*already reconsidering*",
      },
      {
        n: 4,
        setup: "Ask if they want to split a dessert",
        punchline: "No thanks, I want my own.",
      },
      {
        n: 5,
        setup: "Five minutes away. Almost there",
        punchline: "*snacks out, show paused, blanket folded back*\nOkay but your five minutes or actual five minutes.",
      },
    ],
  },
  {
    name: "Gemini", symbol: "\u264A\uFE0E", dates: "May 21 \u2013 Jun 20",
    element: "Air", color: "#F0895A", article: "a",
    image: "/SVG/zodiyak-gemini.svg",
    memes: [
      {
        n: 1,
        setup: "Suggest a simple low-key night in",
        punchline: "Perfect. That's exactly what I need.\n*two minutes later*\nSo what if we went out though.",
      },
      {
        n: 2,
        setup: "Only twenty minutes to talk",
        punchline: "Awesome I only need five.\n*forty minutes later*",
      },
      {
        n: 3,
        setup: "One word reply to a three-paragraph text",
        punchline: "Okay.\n*sends seven follow-ups*",
      },
      {
        n: 4,
        setup: "Ask them to keep a secret",
        punchline: "Obviously. Who would I even tell.\n*tells one person*\nBut…doesn't really count because she already kind of knew.",
      },
      {
        n: 5,
        setup: "Ask them to pick a playlist",
        punchline: "Okay so this one but skip the first three songs. Actually start at track six. Noooo wait go back, I know. Okay stay on this one. Hold on I have to show you something first.",
      },
    ],
  },
  {
    name: "Cancer", symbol: "\u264B\uFE0E", dates: "Jun 21 \u2013 Jul 22",
    element: "Water", color: "#F5A070", article: "a",
    image: "/SVG/zodiyak-cancer.svg",
    memes: [
      {
        n: 1,
        setup: "Three hours, no text back",
        punchline: "It's fine. I was just checking if you were okay. I'm not upset. I just noticed. It's fine.\nNOT FINE",
      },
      {
        n: 2,
        setup: "Forget to mention something important until almost too late",
        punchline: "*very calm*\nNo it's fine. I already figured it out.\n*had been quietly handling it alone for days*",
      },
      {
        n: 3,
        setup: "Ask why their place is so comfortable",
        punchline: "*big smile*\nI just like it this way.\n*rearranged it four times this month…still off*",
      },
      {
        n: 4,
        setup: "Cancel two days before a plan made three weeks ago",
        punchline: "No totally. I get it. Life happens.\n*already planned you missing it*",
      },
      {
        n: 5,
        setup: "Show up hungry but haven't mentioned it",
        punchline: "*already in the kitchen*\nWhat do you want. I have soup. Do you want soup. I'm making soup.",
      },
    ],
  },
  {
    name: "Leo", symbol: "\u264C\uFE0E", dates: "Jul 23 \u2013 Aug 22",
    element: "Fire", color: "#F5B060", article: "a",
    image: "/SVG/zodiyak-leo.svg",
    memes: [
      {
        n: 1,
        setup: "Walk in and nobody reacts",
        punchline: "*stands there one extra blink*\n...Hey.",
      },
      {
        n: 2,
        setup: "Tell them the group decided to go a different direction",
        punchline: "*pause*\nWhose direction.",
      },
      {
        n: 3,
        setup: "Show them a photo of themselves they don't like",
        punchline: "Delete that.\nRight now.\nI'm serious.",
      },
      {
        n: 4,
        setup: "Ask for help getting ready for a date",
        punchline: "*immediately invested*\nOkay sit down. What are we working with. No, not that shirt. Do you have something with more — okay come here.",
      },
      {
        n: 5,
        setup: "Someone at the party didn't know who they were",
        punchline: "*blinks*\nWhat do you mean they didn't — how long were they there.",
      },
    ],
  },
  {
    name: "Virgo", symbol: "\u264D\uFE0E", dates: "Aug 23 \u2013 Sep 22",
    element: "Earth", color: "#F5C070", article: "a",
    image: "/SVG/zodiyak-virgo.svg",
    memes: [
      {
        n: 1,
        setup: "\"Kind of\" cleaned before they came over",
        punchline: "Place looks great.\n*loses track of how much is out of place in seconds*",
      },
      {
        n: 2,
        setup: "Ask for a quick proofread",
        punchline: "Okay so this is good but in the second paragraph you said \"less\" when you meant \"fewer\" and the comma after \"however\" should be — do you want me to just fix it? I'll just fix it.",
      },
      {
        n: 3,
        setup: "Been tired lately, not sure why",
        punchline: "How's your water intake? Are you sleeping before midnight? Have you been outside today? When's the last time you had a full meal?\n*opens notes app*",
      },
      {
        n: 4,
        setup: "Details can get figured out later",
        punchline: "*physically uncomfortable, almost twitching*\nWhat details? What does later mean? Like today later or in a few minutes—",
      },
      {
        n: 5,
        setup: "Winged an important presentation",
        punchline: "*long pause*\nAnd it went well?\n*still processing*",
      },
    ],
  },
  {
    name: "Libra", symbol: "\u264E\uFE0E", dates: "Sep 23 \u2013 Oct 22",
    element: "Air", color: "#F5A0A0", article: "a",
    image: "/SVG/zodiyak-libra.svg",
    memes: [
      {
        n: 1,
        setup: "Ask where they want to eat",
        punchline: "Oh I don't mind, whatever you want.\n*minds*\nWell, not that place.\nWhat are my options.",
      },
      {
        n: 2,
        setup: "Ask if they're mad",
        punchline: "What? No. Why would I be mad. I'm not mad.\n*is a little bit mad*\nIt's just — no. It's fine. I'm not mad.",
      },
      {
        n: 3,
        setup: "RSVP by Friday",
        punchline: "Yeah for sure. Can I let you know Thursday?\n*texts Thursday night at 11:47pm*",
      },
      {
        n: 4,
        setup: "Ask for their honest opinion",
        punchline: "Okay so. I think it's really interesting. Like genuinely. There are things I love about it.\n*has concerns*",
      },
      {
        n: 5,
        setup: "Two friends don't get along. Pick a side",
        punchline: "*visible stress*\nI really think if everyone just sat down together—\nNo, I hear you. I just think—\nCan we not make me do this.",
      },
    ],
  },
  {
    name: "Scorpio", symbol: "\u264F\uFE0E", dates: "Oct 23 \u2013 Nov 21",
    element: "Water", color: "#EE7090", article: "a",
    image: "/SVG/zodiyak-scorpio.svg",
    memes: [
      {
        n: 1,
        setup: "Ask to use their phone for one second",
        punchline: "*hands it over with the screen already off*",
      },
      {
        n: 2,
        setup: "Their ex seemed nice, ran into them earlier",
        punchline: "*looks up*\nWhat did they say.",
      },
      {
        n: 3,
        setup: "Only known each other a few months, still getting to know each other",
        punchline: "*already knows everything*\nRight. Yeah. Still getting to know each other.",
      },
      {
        n: 4,
        setup: "No drama in your life whatsoever",
        punchline: "*unconvinced silence*",
      },
      {
        n: 5,
        setup: "Went quiet for two days",
        punchline: "I wasn't quiet. I was busy.\n*was not busy*",
      },
    ],
  },
  {
    name: "Sagittarius", symbol: "\u2650\uFE0E", dates: "Nov 22 \u2013 Dec 21",
    element: "Fire", color: "#E85878", article: "a",
    image: "/SVG/zodiyak-sagittarius.svg",
    memes: [
      {
        n: 1,
        setup: "Ask them to tone it down a little",
        punchline: "*tones it down for eight minutes*",
      },
      {
        n: 2,
        setup: "Tell them something in confidence, please don't say anything",
        punchline: "Obviously. I would never.\n*tells it as a hypothetical*",
      },
      {
        n: 3,
        setup: "Ask what they meant by that thing they said earlier",
        punchline: "I mean I was just being honest. I didn't mean it in a bad way. You know how I am.",
      },
      {
        n: 4,
        setup: "Make a five year plan together",
        punchline: "Yeah totally.\n*has not thought about next month*",
      },
      {
        n: 5,
        setup: "Ask them to be somewhere at noon",
        punchline: "Yeah noon, I'll be there.\n*arrives at 12:34*\nI said around noon.",
      },
    ],
  },
  {
    name: "Capricorn", symbol: "\u2651\uFE0E", dates: "Dec 22 \u2013 Jan 19",
    element: "Earth", color: "#E06868", article: "a",
    image: "/SVG/zodiyak-capricorn.svg",
    memes: [
      {
        n: 1,
        setup: "Called in sick, mental health day",
        punchline: "*pause*\nGood for you.\n*has never called in sick*",
      },
      {
        n: 2,
        setup: "Ask what they did all weekend",
        punchline: "Worked on some things.\n*worked on everything*",
      },
      {
        n: 3,
        setup: "Plan changed last minute",
        punchline: "*very calm*\nOkay. What's the new plan.\n*internally recalculating*",
      },
      {
        n: 4,
        setup: "Just relax tonight, no work",
        punchline: "I'm relaxed.\n*checks email*\nI'm completely relaxed.",
      },
      {
        n: 5,
        setup: "Just see how it goes, no stress about the outcome",
        punchline: "*nods*\n*makes a spreadsheet anyway*",
      },
    ],
  },
  {
    name: "Aquarius", symbol: "\u2652\uFE0E", dates: "Jan 20 \u2013 Feb 18",
    element: "Air", color: "#F09080", article: "an",
    image: "/SVG/zodiyak-aquarius.svg",
    memes: [
      {
        n: 1,
        setup: "Tell them their opinion is kind of unusual",
        punchline: "*pleased*\nThank you.",
      },
      {
        n: 2,
        setup: "Ask why they didn't come to the party",
        punchline: "I was there for a little while. I just don't really do the whole—\nIt was fine. I just had a thing.\n*did not have a thing*",
      },
      {
        n: 3,
        setup: "Ask them to just go along with it, no questions",
        punchline: "I just want to understand the reasoning. I'm not being difficult. I just think if we examined the premise—",
      },
      {
        n: 4,
        setup: "Tell them everyone does it this way",
        punchline: "*the opposite of persuaded*",
      },
      {
        n: 5,
        setup: "Ask why they seem distant sometimes",
        punchline: "I'm not distant. I'm just thinking.\n*has been thinking for three days*",
      },
    ],
  },
  {
    name: "Pisces", symbol: "\u2653\uFE0E", dates: "Feb 19 \u2013 Mar 20",
    element: "Water", color: "#F0B0A0", article: "a",
    image: "/SVG/zodiyak-pisces.svg",
    memes: [
      {
        n: 1,
        setup: "Tell them you're fine, don't worry",
        punchline: "*already worried*\nOkay. I just want you to know I'm here.\n*sends a check-in text in two hours*",
      },
      {
        n: 2,
        setup: "Crying at a commercial",
        punchline: "*waves hand*\nI'm fine. It's just — they've been together for forty years and she didn't even —\n*cannot finish the sentence*",
      },
      {
        n: 3,
        setup: "Cancel plans, just need a night alone",
        punchline: "Of course. Take all the time you need.\n*spends the night imagining every possible reason*",
      },
      {
        n: 4,
        setup: "Ask what they're thinking about",
        punchline: "*three seconds of silence*\nNothing.\n*was thinking about a conversation from 2019*",
      },
      {
        n: 5,
        setup: "The movie doesn't have a happy ending",
        punchline: "*watches anyway*\n*fully devastated*\n*knew this would happen*\nI just thought maybe this time—",
      },
    ],
  },
];
